import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { 
  ArrowLeft,
  User,
  FileText,
  Star,
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  TrendingUp,
  Clock,
  Download,
  UserPlus,
  Wrench,
  ShieldAlert
} from 'lucide-react';
import { cn } from "@/lib/utils";
import type { BulkAnalysisResult } from './types/bulk';

interface BulkAnalysisDetailsProps {
  candidate: BulkAnalysisResult;
  onBack: () => void;
  onAddToProject?: (candidate: BulkAnalysisResult) => void;
  projectId?: string;
}

export function BulkAnalysisDetails({ 
  candidate, 
  onBack, 
  onAddToProject, 
  projectId 
}: BulkAnalysisDetailsProps) {
  
  const getScoreColor = (score: number) => {
    if (score >= 8) return "text-green-600";
    if (score >= 6) return "text-blue-600";
    if (score >= 4) return "text-yellow-600";
    return "text-red-600";
  };

  const getVerdictDisplay = (verdict: BulkAnalysisResult['verdict']) => {
    switch (verdict) {
      case 'strong_fit':
        return {
          color: 'text-green-600',
          iconColor: 'text-green-600',
          icon: <CheckCircle className="h-5 w-5" />,
          title: 'Strong Fit'
        };
      case 'potential_fit':
        return {
          color: 'text-blue-600',
          iconColor: 'text-blue-600',
          icon: <Star className="h-5 w-5" />,
          title: 'Potential Fit'
        };
      case 'needs_consideration':
        return {
          color: 'text-amber-600',
          iconColor: 'text-amber-600',
          icon: <AlertTriangle className="h-5 w-5" />,
          title: 'Needs Consideration'
        };
      case 'not_recommended':
        return {
          color: 'text-red-600',
          iconColor: 'text-red-600',
          icon: <XCircle className="h-5 w-5" />,
          title: 'Not Recommended'
        };
      default:
        return {
          color: 'text-slate-600',
          iconColor: 'text-slate-600',
          icon: <FileText className="h-5 w-5" />,
          title: 'Unknown'
        };
    }
  };

  const verdictDisplay = getVerdictDisplay(candidate.verdict);

  if (candidate.error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Results
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Analysis Error
          </h1>
        </div>

        <Card className="border-red-200 bg-red-50 dark:bg-red-900/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <XCircle className="h-8 w-8 text-red-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">
                  Failed to Analyze: {candidate.candidateName}
                </h3>
                <p className="text-red-700 dark:text-red-300 mb-4">
                  {candidate.error}
                </p>
                <div className="text-sm text-red-600 dark:text-red-400">
                  <p><strong>File:</strong> {candidate.fileName}</p>
                  <p><strong>Processing Time:</strong> {candidate.processingTime}ms</p>
                  <p><strong>Analysis Date:</strong> {candidate.analysisDate.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Results
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <User className="h-6 w-6" />
              {candidate.candidateName}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Analysis Report • {candidate.fileName}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {projectId && onAddToProject && (
            <Button 
              onClick={() => onAddToProject(candidate)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add to Project
            </Button>
          )}
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Score and Verdict Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Score Card */}
        <Card className="md:col-span-1 flex flex-col items-center justify-center text-center p-8 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl shadow-sm border border-slate-200/60">
          <span className={cn("text-7xl font-bold", getScoreColor(candidate.overallScore))}>
            {candidate.overallScore.toFixed(1)}
          </span>
          <span className="mt-2 text-sm font-semibold text-slate-600 tracking-wide">FIT SCORE</span>
        </Card>

        {/* Verdict Card */}
        <Card className="md:col-span-2 rounded-2xl shadow-sm border border-slate-200/60 bg-white/95 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-bold text-slate-900 tracking-tight">Verdict & Reasoning</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <div className={cn("h-5 w-5 flex-shrink-0", verdictDisplay.iconColor)}>
                  {verdictDisplay.icon}
                </div>
                <h4 className={cn("font-semibold", verdictDisplay.color)}>
                  {verdictDisplay.title}
                </h4>
              </div>
              
              {candidate.reasoning && candidate.reasoning.length > 0 && (
                <ul className="space-y-1.5 pl-5 text-sm list-disc list-outside">
                  {candidate.reasoning.map((reason, index) => (
                    <li key={index} className="text-slate-700">
                      {reason}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>
      </div>



      {/* Detailed Analysis */}
      <Accordion type="multiple" defaultValue={['skills', 'strengths']} className="w-full space-y-6">
        
        {/* Skills Analysis */}
        <AccordionItem value="skills" className="border border-slate-200/60 rounded-2xl overflow-hidden bg-white/95 backdrop-blur-sm shadow-sm">
          <AccordionTrigger className="px-6 sm:px-8 py-6 hover:bg-slate-50/50 focus-visible:bg-slate-50/50 text-base font-semibold group transition-all duration-200">
            <div className="flex items-center space-x-4">
              <div className="h-8 w-8 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors duration-200">
                <Wrench className="h-4 w-4 text-purple-600" />
              </div>
              <span>Skills Analysis</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 sm:px-8 py-6 border-t border-slate-100 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Matched Skills */}
              <div>
                <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Matched Skills ({candidate.matchedSkills.length})
                </h4>
                <div className="space-y-2">
                  {candidate.matchedSkills.map((skill, index) => (
                    <Badge key={index} variant="outline" className="mr-2 mb-2 bg-green-50 border-green-200 text-green-800">
                      {skill}
                    </Badge>
                  ))}
                  {candidate.matchedSkills.length === 0 && (
                    <p className="text-sm text-slate-500">No matched skills identified</p>
                  )}
                </div>
              </div>

              {/* Missing Skills */}
              <div>
                <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Missing Skills ({candidate.missingSkills.length})
                </h4>
                <div className="space-y-2">
                  {candidate.missingSkills.map((skill, index) => (
                    <Badge key={index} variant="outline" className="mr-2 mb-2 bg-red-50 border-red-200 text-red-800">
                      {skill}
                    </Badge>
                  ))}
                  {candidate.missingSkills.length === 0 && (
                    <p className="text-sm text-slate-500">No missing skills identified</p>
                  )}
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Strengths & Weaknesses */}
        <AccordionItem value="strengths" className="border border-slate-200/60 rounded-2xl overflow-hidden bg-white/95 backdrop-blur-sm shadow-sm">
          <AccordionTrigger className="px-6 sm:px-8 py-6 hover:bg-slate-50/50 focus-visible:bg-slate-50/50 text-base font-semibold group transition-all duration-200">
            <div className="flex items-center space-x-4">
              <div className="h-8 w-8 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors duration-200">
                <TrendingUp className="h-4 w-4 text-green-600" />
              </div>
              <span>Strengths & Weaknesses</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 sm:px-8 py-6 border-t border-slate-100 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Strengths */}
              <div>
                <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <Star className="h-4 w-4" />
                  Key Strengths
                </h4>
                <ul className="space-y-2">
                  {candidate.strengths.map((strength, index) => (
                    <li key={index} className="text-sm text-slate-700 flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      {strength}
                    </li>
                  ))}
                  {candidate.strengths.length === 0 && (
                    <p className="text-sm text-slate-500">No specific strengths highlighted</p>
                  )}
                </ul>
              </div>

              {/* Weaknesses */}
              <div>
                <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4" />
                  Areas for Improvement
                </h4>
                <ul className="space-y-2">
                  {candidate.weaknesses.map((weakness, index) => (
                    <li key={index} className="text-sm text-slate-700 flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      {weakness}
                    </li>
                  ))}
                  {candidate.weaknesses.length === 0 && (
                    <p className="text-sm text-slate-500">No specific weaknesses identified</p>
                  )}
                </ul>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Analysis Metadata */}
        <AccordionItem value="metadata" className="border border-slate-200/60 rounded-2xl overflow-hidden bg-white/95 backdrop-blur-sm shadow-sm">
          <AccordionTrigger className="px-6 sm:px-8 py-6 hover:bg-slate-50/50 focus-visible:bg-slate-50/50 text-base font-semibold group transition-all duration-200">
            <div className="flex items-center space-x-4">
              <div className="h-8 w-8 bg-slate-100 rounded-xl flex items-center justify-center group-hover:bg-slate-200 transition-colors duration-200">
                <Clock className="h-4 w-4 text-slate-600" />
              </div>
              <span>Analysis Details</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 sm:px-8 py-6 border-t border-slate-100 bg-white">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-slate-500">File Name</span>
                <p className="text-slate-900 mt-1">{candidate.fileName}</p>
              </div>
              <div>
                <span className="font-medium text-slate-500">Analysis Date</span>
                <p className="text-slate-900 mt-1">
                  {candidate.analysisDate.toLocaleDateString()} at {candidate.analysisDate.toLocaleTimeString()}
                </p>
              </div>
              <div>
                <span className="font-medium text-slate-500">Processing Time</span>
                <p className="text-slate-900 mt-1">{candidate.processingTime}ms</p>
              </div>
              <div>
                <span className="font-medium text-slate-500">Candidate ID</span>
                <p className="text-slate-900 mt-1 font-mono text-xs">{candidate.id}</p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

      </Accordion>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-6 border-t">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Results
        </Button>
        
        <div className="flex gap-3">
          {projectId && onAddToProject && (
            <Button 
              onClick={() => onAddToProject(candidate)}
              variant="outline"
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add to Project
            </Button>
          )}
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>
    </div>
  );
} 